import Modal from 'particles/Modal'
import styled from 'particles/themes/styled'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { selectRequestsAreLoading } from 'state'
import {
    PartiposterTagType,
    clearPartiposterTagList,
    clearTagModalErrors,
    closeTagModal,
    getPartiposterTagListRequest,
    savePartiposterTagRequest,
    selectPartiposterTags,
    selectTagModal,
} from 'state/partiposter'
import { STATE_KEYS } from 'state/requests'
import UserProfile from './UserProfile'
import { FormikTextArea } from 'particles/TextInputs/TextArea'
import RectangleButton from 'particles/Button/RectangleButton'
import { FieldArray, FieldArrayRenderProps, Formik, FormikProps } from 'formik'
import * as yup from 'yup'
import IconButton from 'particles/Button/IconButton'
import _ from 'lodash'
import Loading from 'components/Loading'
import SubmissionTagsSelector from './SubmissionTagsSelector'
import { selectTagById } from 'state/constants'
import Text from 'particles/Text'
import RichInfoTag, { getSeverityState } from 'particles/Tag'
import { TAG_LABELS } from '.'

interface ProfileTag {
    uuid: string
    text: string
}

interface ProfileTags {
    profileTags: ProfileTag[]
}

interface AddModalProps {
    className?: string
}

const AddModal: React.FC<AddModalProps> = ({ className }) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const { isOpen, selectedProfile, campaignUuid, submissionUuid, errors } = useSelector(selectTagModal)
    const isLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.GetPartiposterTagAddList]))
    const isSaving = useSelector(selectRequestsAreLoading([STATE_KEYS.SavePartiposterTag]))
    const partiposterTags = useSelector(selectPartiposterTags(PartiposterTagType.ADD))
    const tagById = useSelector(selectTagById)

    useEffect(() => {
        if (selectedProfile) {
            dispatch(
                getPartiposterTagListRequest({
                    profileUuid: selectedProfile.uuid,
                    campaign: campaignUuid,
                    target: 'submission',
                    type: PartiposterTagType.ADD,
                }),
            )
        }
        return () => {
            dispatch(clearPartiposterTagList({ type: PartiposterTagType.ADD }))
        }
    }, [selectedProfile])

    const onClose = () => dispatch(closeTagModal())

    const showCampaignTagged: boolean = partiposterTags && partiposterTags.length > 0

    return isOpen ? (
        <Formik
            initialValues={{ profileTags: [{ uuid: '', text: '' }] }}
            initialErrors={{ profileTags: errors }}
            enableReinitialize
            validationSchema={yup.object({
                profileTags: yup.array().of(
                    yup.object({
                        uuid: yup
                            .string()
                            .nullable()
                            .required(t('errorMessage.validation.selectTag', 'Please select a tag.')),
                        text: yup.string().nullable().notRequired(),
                    }),
                ),
            })}
            onSubmit={values => {
                dispatch(clearTagModalErrors())
                dispatch(
                    savePartiposterTagRequest({
                        tags: values.profileTags,
                        submissionUuid,
                        profileUuid: selectedProfile.uuid,
                    }),
                )
            }}
        >
            {({ values, handleSubmit }: FormikProps<ProfileTags>) => (
                <Modal
                    className={className}
                    hasIcon={false}
                    title={t('modalWindow.addPartiposterTagTitle')}
                    subtext={t('modalWindow.addPartiposterTagSubtext')}
                    onClose={onClose}
                    size="m"
                    primaryButtonProps={{
                        label: t('button.addTags'),
                        loadingText: t('button.addTags'),
                        isLoading: isSaving,
                    }}
                    onConfirm={() => handleSubmit()}
                    secondaryButtonProps={{
                        label: t('button.cancel'),
                        isDisabled: isSaving,
                    }}
                >
                    <div className="tags-container">
                        <UserProfile profileData={selectedProfile} />
                        {isLoading ? (
                            <Loading />
                        ) : (
                            <>
                                {showCampaignTagged && (
                                    <div className="campaign-tag-container">
                                        <Text scale="bodyS500">{t('submission.tag.overviewTagsTitle')}</Text>
                                        <div className="tag-container">
                                            {partiposterTags.map((partiposterTag, index) => {
                                                const tag = tagById[partiposterTag.tag_uuid]
                                                return (
                                                    <RichInfoTag
                                                        key={`campaign-tag-${index}`}
                                                        contentType={getSeverityState(tag.severity)}
                                                        label={tag.is_custom ? tag.name : TAG_LABELS[tag.name]}
                                                    />
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}
                                <div className="line" />
                                <FieldArray
                                    name="profileTags"
                                    validateOnChange={false}
                                    render={(arrayHelpers: FieldArrayRenderProps) => (
                                        <>
                                            {values.profileTags.map((__: any, index: number) => (
                                                <div key={index}>
                                                    {values.profileTags.length > 1 && (
                                                        <IconButton
                                                            size="xs"
                                                            color="neutral"
                                                            form="icon"
                                                            icon="x"
                                                            className="remove-tag"
                                                            onClick={() => arrayHelpers.remove(index)}
                                                            isDisabled={isSaving}
                                                        />
                                                    )}
                                                    <div className="tags-container">
                                                        <SubmissionTagsSelector
                                                            fieldName={`profileTags.${index}.uuid`}
                                                        />
                                                        <FormikTextArea
                                                            name={`profileTags.${index}.text`}
                                                            size="s"
                                                            label={t('submission.tagHistory.notes')}
                                                            isDisabled={
                                                                _.isEmpty(values.profileTags[index].uuid) || isSaving
                                                            }
                                                        />
                                                        {values.profileTags.length > 1 && <div className="line" />}
                                                    </div>
                                                </div>
                                            ))}
                                            <div>
                                                <RectangleButton
                                                    label={t('button.addMore')}
                                                    form="outlined"
                                                    icon="plus"
                                                    onClick={() => arrayHelpers.push({ uuid: '', text: '' })}
                                                    isDisabled={isSaving}
                                                />
                                            </div>
                                        </>
                                    )}
                                />
                            </>
                        )}
                    </div>
                </Modal>
            )}
        </Formik>
    ) : null
}

export default styled(AddModal)`
    .tags-container {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[400]};
        .campaign-tag-container {
            display: flex;
            flex-direction: column;
            gap: ${({ theme }) => theme.spacing[100]};
            .tag-container {
                display: flex;
                flex-wrap: wrap;
                gap: ${({ theme }) => theme.spacing[200]};
            }
        }
        .remove-tag {
            margin-left: auto;
        }
        .line {
            border-width: 0px;
            border-top-width: 1px;
            border-style: solid;
            border-color: ${({ theme }) => theme.colors.divider.primary};
        }
    }
`
